import { useMemo, useRef, useState } from "react";
import { MapPin, ChevronDown, Search, Check, X } from "lucide-react";
import { useCity } from "@/context/CityContext";
import Popover from "@/components/Popover";

const POPULAR = ["Belgaum", "Bengaluru", "Mumbai", "Pune", "New Delhi", "Hyderabad", "Chennai", "Kolkata", "Jaipur", "Udaipur", "Goa", "Panaji", "Hubli", "Dharwad", "Kolhapur", "Mysore", "Mangalore", "Ahmedabad", "Lucknow", "Chandigarh"];

/**
 * Searchable city picker. value/onChange are controlled with the city name.
 * - allowAll adds an "All cities" row
 * - state limits the list to one state
 * - variant: "pill" (default), "field" (form input look) or "bare" (inside the search bar)
 */
export default function CitySelect({ value, onChange, allowAll = false, placeholder = "Choose city", className = "", testId = "city-select", size = "md", state = null, variant = "pill" }) {
  const { cities } = useCity();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const anchor = useRef(null);

  const pool = useMemo(() => (state ? cities.filter((c) => c.state === state) : cities), [cities, state]);
  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (t) {
      const starts = [], contains = [];
      for (const c of pool) {
        const n = c.name.toLowerCase();
        if (n.startsWith(t)) starts.push(c);
        else if (n.includes(t) || (c.state || "").toLowerCase().startsWith(t)) contains.push(c);
        if (starts.length > 150) break;
      }
      return [...starts, ...contains].slice(0, 150);
    }
    if (state) return pool.slice(0, 200);
    const pop = POPULAR.map((n) => pool.find((c) => c.name === n)).filter(Boolean);
    return pop.length ? pop : pool.slice(0, 60);
  }, [pool, q, state]);

  const pick = (name) => { onChange(name); setOpen(false); setQ(""); };
  const pad = size === "lg" ? "px-5 py-3.5 text-base" : "px-4 py-2.5 text-[15px]";
  const trigger =
    variant === "inline" ? `inline-field inline-flex items-center gap-1 text-left max-w-full`
    : variant === "bare" ? `w-full inline-flex items-center justify-between gap-2 py-1 text-left`
    : variant === "field" ? `w-full inline-flex items-center justify-between gap-2 px-4 py-3 rounded-[14px] bg-white border border-[color:var(--line)] hover:border-[color:var(--maroon)] text-[15px]`
    : `w-full inline-flex items-center justify-between gap-2 ${pad} rounded-full bg-white border border-[color:var(--line)] hover:border-[color:var(--maroon)] transition-colors`;

  return (
    <div className={`relative ${variant === "inline" ? "inline-block align-baseline max-w-full" : ""} ${className}`}>
      <button ref={anchor} type="button" onClick={() => setOpen(!open)} className={trigger} data-testid={testId} aria-haspopup="listbox" aria-expanded={open}>
        <span className="inline-flex items-center gap-2 min-w-0">
          {variant !== "bare" && variant !== "inline" && <MapPin className="w-4 h-4 text-[color:var(--maroon)] shrink-0"/>}
          <span className={`truncate ${variant === "inline" ? (value ? "" : "inline-placeholder") : value ? "text-[color:var(--ink)] font-semibold" : "text-neutral-500"} ${variant === "bare" ? "text-[16px]" : ""}`}>{value || (allowAll ? "All cities" : placeholder)}</span>
        </span>
        <ChevronDown className={`${variant === "inline" ? "w-[0.6em] h-[0.6em]" : "w-4 h-4"} shrink-0 text-[color:var(--maroon)] transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      <Popover open={open} anchorRef={anchor} onClose={() => { setOpen(false); setQ(""); }} minWidth={300} testId={`${testId}-panel`}>
        <div className="p-3 border-b border-[color:var(--line)] bg-[color:var(--cream)] shrink-0">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500"/>
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); if (list[0]) pick(list[0].name); } }} placeholder={`Search ${pool.length.toLocaleString("en-IN")} cities or a state…`} className="w-full pl-9 pr-8 py-2.5 rounded-xl bg-white border border-[color:var(--line)] text-[15px] outline-none focus:border-[color:var(--maroon)]" data-testid={`${testId}-search`}/>
            {q && <button type="button" onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-neutral-400" aria-label="Clear"><X className="w-4 h-4"/></button>}
          </div>
        </div>
        <div className="overflow-y-auto overscroll-contain" role="listbox">
          {allowAll && (
            <button type="button" onClick={() => pick("")} className="w-full text-left px-4 py-2.5 text-[15px] hover:bg-[color:var(--ivory)] flex justify-between items-center border-b border-[color:var(--line)]/60" data-testid={`${testId}-all`}>
              <span className="font-semibold">All cities</span>{!value && <Check className="w-4 h-4 text-[color:var(--maroon)]"/>}
            </button>
          )}
          {!q && !state && <div className="px-4 pt-3 pb-1 text-xs text-neutral-500">Popular — type to search every city</div>}
          {list.map((c) => (
            <button type="button" key={`${c.name}|${c.state}`} onClick={() => pick(c.name)} className="w-full text-left px-4 py-2.5 text-[15px] hover:bg-[color:var(--ivory)] flex justify-between items-center gap-3" data-testid={`${testId}-opt-${c.name}`}>
              <span className="text-[color:var(--ink)] truncate">{c.name}</span>
              <span className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-neutral-400">{c.state}</span>
                {value === c.name && <Check className="w-4 h-4 text-[color:var(--maroon)]"/>}
              </span>
            </button>
          ))}
          {!list.length && <div className="px-4 py-10 text-center text-sm text-neutral-500">No city matches “{q}”</div>}
        </div>
      </Popover>
    </div>
  );
}
